import { Stack, useMediaQuery } from '@mui/material';
import { useMemo } from 'react';

import { useClaimsQuery } from '../../../api/claims/getClaims';
import { getClaimsMarketingCards } from '../../../components/InfoCards';
import PhoneNumberLink from '../../../components/styledComponents/PhoneNumberLink';
import StatusAlert from '../../../components/styledComponents/StatusAlert';
import { StatusType } from '../../../constants/enums';
import useUpdateTitle from '../../../hooks/useUpdateTitle';
import theme from '../../../styles/theme';
import ClaimCard from './ClaimCard';
import { usePoliciesQuery } from '../../../api/getPolicies';
import { useBillsQuery } from '../../../api/getBills';
import CircularProgress from '../../../components/CircularProgress/CircularProgress';

export interface ClaimCardsProps {
  ubiEnabled?: boolean;
  unenrolledPaperlessPolicy?: boolean;
}

const ClaimCards = ({
  ubiEnabled,
  unenrolledPaperlessPolicy,
}: ClaimCardsProps) => {
  useUpdateTitle('Claims');
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));
  const { isLoading, data, error } = useClaimsQuery();
  const { data: billsData } = useBillsQuery();
  const { data: policyData } = usePoliciesQuery();
  const crossSellInfo = policyData?.crossSellInfo;
  const showAutoHomeCard = crossSellInfo?.crossSellEligible && !!billsData;

  const ClaimsMarketingCards = useMemo(
    () =>
      getClaimsMarketingCards(
        ubiEnabled,
        unenrolledPaperlessPolicy,
        showAutoHomeCard
      ),
    [ubiEnabled, unenrolledPaperlessPolicy, showAutoHomeCard]
  );
  const mobileClaims =
    data?.claims && data?.claims?.length > 0
      ? data.claims
      : [...Array(ClaimsMarketingCards.length)];

  const claims = data?.claims;

  const claimsBannerText =
    'New auto, property or personal umbrella claims may take up to 24 hours to appear in MyPolicy.';

  const mobileCards = mobileClaims?.flatMap((e, idx) => {
    const MarketingCard = ClaimsMarketingCards[idx] ?? null;

    if (idx < ClaimsMarketingCards.length && idx >= mobileClaims?.length - 1) {
      return [
        <MarketingCard
          key={`claims-marketing-${idx}`}
          data-testid={`claims-marketing-${idx}`}
        />,
        ...(idx === 0
          ? [
              <StatusAlert
                key="claims-alert"
                open
                text={claimsBannerText}
                status={StatusType.INFO}
              />,
            ]
          : []),
        ...(e
          ? [
              <ClaimCard
                key={`claim-${idx}-${e.claimNumber}`}
                claim={e}
                data-testid={`claim-card-${e.claimNumber}`}
              />,
            ]
          : []),
        ClaimsMarketingCards.slice(idx + 1).map(
          (ClaimsMarketingCard, index) => (
            <ClaimsMarketingCard
              key={`claims-marketing-${index}`}
              data-testid={`claims-marketing-${index}`}
            />
          )
        ),
      ];
    } else {
      return ClaimsMarketingCards[idx]
        ? [
            <MarketingCard
              key={`claims-marketing-${idx}`}
              data-testid={`claims-marketing-${idx}`}
            />,
            ...(idx === 0
              ? [
                  <StatusAlert
                    key="claims-alert"
                    open
                    text={claimsBannerText}
                    status={StatusType.INFO}
                  />,
                ]
              : []),
            ...(e
              ? [
                  <ClaimCard
                    key={`claim-${idx}-${e.claimNumber}`}
                    claim={e}
                    data-testid={`claim-card-${e.claimNumber}`}
                  />,
                ]
              : []),
          ]
        : [
            ...(idx === 0
              ? [
                  <StatusAlert
                    key="claims-alert"
                    open
                    text={claimsBannerText}
                    status={StatusType.INFO}
                  />,
                ]
              : []),
            <ClaimCard
              key={`claim-${idx}-${e.claimNumber}`}
              claim={e}
              data-testid={`claim-card-${e.claimNumber}`}
            />,
          ];
    }
  });

  const claimsCards =
    claims?.map((claim, idx) => (
      <ClaimCard
        key={`claim-${idx}-${claim.claimNumber}`}
        claim={claim}
        data-testid={`claim-card-${claim.claimNumber}`}
      />
    )) ?? [];

  if (isLoading) {
    return (
      <Stack
        sx={{
          alignItems: "center",
          justifyContent: "center",
          width: 1
        }}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  return (
    <>
      {error && (
        <StatusAlert
          key="bills-load-error"
          open
          title="This information is currently unavailable."
          text={
            <>
              Try again later or call &nbsp;
              <PhoneNumberLink phoneNumber="8889805650" />.
            </>
          }
          status={StatusType.ERROR}
        />
      )}
      {isMobile
        ? [mobileCards]
        : [
            <StatusAlert
              key="claims-alert"
              open
              text={claimsBannerText}
              status={StatusType.INFO}
            />,
            ...claimsCards,
          ]}
    </>
  );
};

export default ClaimCards;
